import React from 'react';
import { Box, Button, Flex, Heading, Text, Markdown, MediaPlayer, VideoPlayerWithTimeline } from '@chatsdet/ui';
import type { VideoTimeline } from '@chatsdet/types';
import type { ThoughtWithTimestamp } from '../../../pages/FlowDesigner/hooks/useWorkflowRunThoughts';
import { ArrowsOut } from '@phosphor-icons/react';
import { DebugPromptButton } from '../DebugPromptButton';

const MARKDOWN_CLASSNAME =
  'prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground/90';

type OverviewTabProps = {
  screenshotSrc: string | null;
  screenshotCaption: string | null;
  videoSrc: string | null;
  videoTimeline: VideoTimeline | null;
  thoughts?: ThoughtWithTimestamp[];
  summaryMarkdown: string | null;
  reproSteps: string | null;
  /**
   * What the test expected to observe. Presence signals the simplified
   * three-section layout (What happened / What was expected / How to
   * reproduce), replacing the legacy repro-steps dump for these issues.
   */
  expectedBehavior: string | null;
  issueId: string;
  apiUrl: string;
  onExpandScreenshot: () => void;
  /** Rendered above the media — the issue Details card + integration links. */
  leadingContent?: React.ReactNode;
};

export function OverviewTab({
  screenshotSrc,
  screenshotCaption,
  videoSrc,
  videoTimeline,
  thoughts,
  summaryMarkdown,
  reproSteps,
  expectedBehavior,
  issueId,
  apiUrl,
  onExpandScreenshot,
  leadingContent,
}: OverviewTabProps) {
  const hasMedia = Boolean(screenshotSrc) || Boolean(videoSrc);

  return (
    <div className="px-6 py-6 space-y-6">
      {leadingContent}
      <section className="space-y-3">
        {hasMedia ? (
          <div className="space-y-2">
            {screenshotCaption && (
              <Text size="sm" className="text-muted-foreground italic">
                {screenshotCaption}
              </Text>
            )}
            <div className="relative">
              {videoSrc ? (
                <VideoPlayerWithTimeline
                  posterSrc={screenshotSrc}
                  videoSrc={videoSrc}
                  timeline={videoTimeline}
                  thoughts={thoughts}
                  alt="Latest occurrence video"
                />
              ) : (
                <>
                  <MediaPlayer
                    posterSrc={screenshotSrc}
                    videoSrc={null}
                    alt="Latest occurrence screenshot"
                    className="max-h-72"
                    aspectRatioClass="aspect-video"
                  />
                  {screenshotSrc && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="absolute top-3 right-3 shadow"
                      iconLeft={<ArrowsOut size={16} />}
                      onPress={onExpandScreenshot}
                    >
                      Expand
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <Box className="w-full h-48 border border-dashed border-border rounded flex items-center justify-center">
            <Text size="sm" className="text-muted-foreground">
              No screenshot available
            </Text>
          </Box>
        )}
      </section>

      {expectedBehavior ? (
        <>
          {summaryMarkdown ? (
            <section className="space-y-3">
              <Heading level={4}>What happened</Heading>
              <Markdown className={MARKDOWN_CLASSNAME}>{summaryMarkdown}</Markdown>
            </section>
          ) : null}

          <section className="space-y-3">
            <Heading level={4}>What was expected</Heading>
            <Markdown className={MARKDOWN_CLASSNAME}>{expectedBehavior}</Markdown>
          </section>

          {reproSteps ? (
            <section className="space-y-3">
              <Heading level={4}>How to reproduce</Heading>
              <Markdown>{reproSteps}</Markdown>
              <div className="pt-2">
                <Flex gap="sm" wrap>
                  <DebugPromptButton issueId={issueId} apiUrl={apiUrl} />
                </Flex>
                <Text size="xs" className="text-muted-foreground mt-2">
                  Copy a prompt that sends your coding agent to debug this issue through Cofactor
                </Text>
              </div>
            </section>
          ) : null}
        </>
      ) : (
        <>
          {summaryMarkdown ? (
            <section className="space-y-3">
              <Heading level={4}>Summary</Heading>
              <Markdown className={MARKDOWN_CLASSNAME}>{summaryMarkdown}</Markdown>
            </section>
          ) : null}

          {reproSteps ? (
            <section className="space-y-3">
              <Heading level={4}>Repro steps</Heading>
              <Markdown>{reproSteps}</Markdown>
              <div className="pt-2">
                <Flex gap="sm" wrap>
                  <DebugPromptButton issueId={issueId} apiUrl={apiUrl} />
                </Flex>
                <Text size="xs" className="text-muted-foreground mt-2">
                  Copy a prompt that sends your coding agent to debug this issue through Cofactor
                </Text>
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
